import type { Control } from "./Control.js";
import type { Point } from "../../common/geometry.js";

/**
 * Something that can say where a named point of it is, in its own coordinates - a 3D stage saying
 * where its model's feet are on screen this frame, say.
 *
 * Duck-typed on purpose: the core does not know about 3D, and a stage living in `webwin/engine3d`
 * satisfies this without either side importing the other.
 */
export interface Pinnable {
  /** Where `anchor` is now, in this control's space; null when it is not on screen at all. */
  pinPoint(anchor: string): Point | null;
}

export function isPinnable(value: unknown): value is Pinnable {
  return typeof (value as Partial<Pinnable> | null)?.pinPoint === "function";
}

export interface PinOptions {
  /** Pixels to shift the pinned control from the point, after centering it on it. */
  dx?: number;
  dy?: number;
  /** Which of the control's own points lands on the anchor. Its center by default. */
  origin?: "center" | "top" | "bottom";
}

/**
 * Keep `control` on a point of `target`, frame after frame - a ring under a hero's feet, a name
 * over its head.
 *
 * The two may be in different containers - the ring in a scene's background band and the hero on
 * its stage - so the point goes out through screen coordinates and back into the control's
 * parent's. It is repositioned before each paint (`Desktop.onFrame`), so it never lags a frame
 * behind what it follows; while the target cannot place the point the control is hidden rather
 * than left wherever it last was.
 *
 * Needs both attached to a desktop. Returns the way to let go.
 */
export function pin(control: Control, target: Control & Pinnable, anchor: string, options: PinOptions = {}): () => void {
  const desktop = control.desktop ?? target.desktop;
  if (!desktop) throw new Error("webwin: pin() needs the control on a desktop first");
  const dx = options.dx ?? 0;
  const dy = options.dy ?? 0;
  const origin = options.origin ?? "center";
  let hiddenByPin = false;

  const step = (): boolean => {
    const parent = control.parent;
    if (!parent || !target.desktop) return false;
    const local = target.pinPoint(anchor);
    if (!local) {
      if (control.visible) {
        control.visible = false;
        hiddenByPin = true;
      }
      return false;
    }
    if (hiddenByPin) {
      control.visible = true;
      hiddenByPin = false;
    }
    const at = parent.fromScreen(target.toScreen(local));
    const clientOrigin = parent.clientRect;
    const x = Math.round(at.x - clientOrigin.x - control.width / 2 + dx);
    const offsetY = origin === "top" ? 0 : origin === "bottom" ? control.height : control.height / 2;
    const y = Math.round(at.y - clientOrigin.y - offsetY + dy);
    if (x !== control.x || y !== control.y) {
      control.setBounds(x, y, control.width, control.height);
      control.invalidate();
    }
    return false;
  };
  step();
  return desktop.onFrame(step);
}
